import { useState, useMemo } from "react";
import { DoorOpen, Star, TriangleAlert } from "lucide-react";
import { ChartCard, ChartEmpty, SortTh, tableClass } from "./ui";
import { cn } from "@/lib/utils";

const rateClass = (rate) =>
  rate >= 20 ? "text-success-700" : rate >= 10 ? "text-warning-700" : "text-destructive";

const RoomPopularityTable = ({ rooms, noAvailabilityCount }: any) => {
  const [sortKey, setSortKey] = useState("times_shown");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    if (!rooms || rooms.length === 0) return [];
    return [...rooms].sort((a, b) => {
      const aVal = a[sortKey] || 0;
      const bVal = b[sortKey] || 0;
      return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });
  }, [rooms, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const noAvailability = noAvailabilityCount > 0 && (
    <p className="no-availability-banner flex items-center gap-2 rounded-lg bg-warning-500/15 px-3 py-2 text-sm text-warning-700">
      <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
      <span><strong>{noAvailabilityCount}</strong> time{noAvailabilityCount !== 1 ? "s" : ""} guests searched but no rooms were available</span>
    </p>
  );

  const card = (body) => (
    <ChartCard icon={<DoorOpen />} title="Room Demand & Booking" description="Which rooms guests look at and book.">{body}</ChartCard>
  );

  if (!rooms || rooms.length === 0) {
    return card(<><ChartEmpty icon={<DoorOpen />} text="No room data for selected period" />{noAvailability}</>);
  }

  const topRoom = sorted[0];
  const sortable = (key, label) => (
    <SortTh label={label} active={sortKey === key} dir={sortDir} onClick={() => handleSort(key)} />
  );

  return card(
    <>
      {noAvailability}
      <div className="usage-table-container overflow-x-auto rounded-lg border border-border">
        <table className={tableClass}>
          <thead className="bg-muted/50">
            <tr>
              <SortTh label="Room" active={false} dir={sortDir} />
              {sortable("times_shown", "Guest Searches")}
              {sortable("times_booked", "Times Booked")}
              {sortable("conversion_rate", "Booking Rate")}
            </tr>
          </thead>
          <tbody>
            {sorted.map((room, idx) => {
              const isTop = room.room_name === topRoom?.room_name;
              return (
                <tr key={idx} className={cn(isTop && "busiest-day-row bg-primary/5")}>
                  <td>
                    <strong>{room.room_name}</strong>
                    {isTop && (
                      <span className="busiest-badge ms-2 inline-flex items-center gap-1 rounded-full bg-warning-500/15 px-2 py-0.5 text-xs font-medium text-warning-700">
                        <Star className="size-3" aria-hidden="true" /> Most Searched
                      </span>
                    )}
                  </td>
                  <td className="num-formatted tabular-nums">{room.times_shown}</td>
                  <td className="num-formatted tabular-nums">{room.times_booked}</td>
                  <td className={cn("num-formatted font-semibold tabular-nums", rateClass(room.conversion_rate))}>{room.conversion_rate}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default RoomPopularityTable;
